import Button from "@/components/Button";
import { useCountryStripeProduct } from "@/src/features/pricing/hooks/useCountryStripeProduct";
import clsx from "clsx";
import Image from "next/image";
import { useMemo } from "react";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";

const PlanComparison = ({ heading, subheading, planFeatures, button }) => {
  const { prices } = useCountryStripeProduct();

  const planPrice = prices?.find((price) => {
    return price.recurring?.interval === "year";
  });

  const metadata = planPrice?.metadata;

  const computedPlanSubheading = useMemo(
    () => metadata?.hero_subheading,
    [planPrice]
  );

  const getFeatureSymbol = (status) => {
    switch (status) {
      case "limited":
        return (
          <div className="text-sm font-medium text-[#F96043]">Limited</div>
        );
      case "available":
        return <CheckCircleIcon className="h-6 w-6 fill-[#7AD691]" />;
      case "notAvailable":
        return <XCircleIcon className="h-6 w-6 fill-[#F96043]" />;
    }
  };

  return (
    <div
      className={clsx(
        "my-16 flex h-full flex-col rounded-2xl outline-1 outline-offset-[-1px] outline-zinc-200 lg:flex-row lg:gap-10 lg:p-10 lg:outline"
      )}
    >
      <div className="flex flex-col lg:max-w-xl lg:justify-between">
        <div>
          <div className="text-center text-xl font-medium leading-snug text-zinc-900 lg:text-left lg:text-3xl">
            {heading}
          </div>
          <div className="lg-font-medium py-4 text-center font-body text-sm font-normal leading-snug text-zinc-700 lg:text-left lg:text-base">
            {computedPlanSubheading ?? subheading}
          </div>
        </div>
        <div className="lg:w-fit">
          {button && button[0] && (
            <Button
              theme="light"
              label={button[0].target.text}
              href={button[0].target.url}
              className="mb-2"
            />
          )}
          <div className="text-center font-body text-sm font-medium leading-snug text-zinc-700 lg:text-base">
            {button?.[0]?.disclaimer}
          </div>
        </div>
      </div>
      <div className="mx-auto mt-12 w-full max-w-2xl text-sm lg:mt-0 lg:text-base">
        {/* Table header */}
        <div className="mb-2 grid grid-cols-[1fr_64px_64px] rounded-t-lg px-5 py-4 text-center font-body font-normal text-zinc-900">
          <div></div>
          <div>Premium</div>
          <div>Free</div>
        </div>
        {/* Table rows */}
        <div className="w-full overflow-hidden rounded-b-lg">
          {planFeatures &&
            planFeatures.map((feature, index) => (
              <div
                key={index}
                className={`grid grid-cols-[1fr_64px_64px] px-5 py-4 font-body ${
                  index % 2 === 0 ? "bg-neutral-50" : "bg-white"
                }`}
              >
                <div className="font-body font-medium text-zinc-700">
                  {feature.feature}
                </div>
                <div className="flex items-center justify-center">
                  {getFeatureSymbol(feature.premium)}
                </div>
                <div className="flex items-center justify-center">
                  {getFeatureSymbol(feature.free)}
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default PlanComparison;
